import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { User } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useSidebarPrivileges } from '@entities/viewer'

import { useUsersStore } from './store'
import { getUserEditPath } from './user-options'

interface UseUserActionsOptions {
  user: ComputedRef<User>
}

interface UserActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManage: ComputedRef<boolean>
  toggle: () => void
  openEdit: () => Promise<void>
  openDelete: () => void
}

export const useUserActions = ({ user }: UseUserActionsOptions): UserActionsReturn => {
  const router = useRouter()
  const store = useUsersStore()
  const { canManageUsers } = useSidebarPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const canManage = computed<boolean>(() => canManageUsers.value)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = async (): Promise<void> => {
    isOpen.value = false
    await router.push(getUserEditPath(user.value.id))
  }

  const openDelete = (): void => {
    isOpen.value = false
    store.setUserToDelete(user.value)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManage,
    toggle,
    openEdit,
    openDelete,
  }
}
